import moment from "moment";

import { AdditionalSignOffInformation } from "@/Common/Components/Modals/LinkingPopup/LinkingPopup";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { FaxLabRequestComposite } from "@/Common/Models/Faxing/FaxLabRequestComposite";
import { Guid } from "@/Common/Models/Guid";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { PortfolioInboxes } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel, TableColumnInfo } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { FaxConsultationRequestComposite } from "@/ExportableFile/Models/FaxConsultationRequestComposite";
import { ScreeningTestStatus } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { PreventiveCareActivitiesHelper } from "@/Portfolio/Helpers/PreventiveCareActivitiesHelper";
import { PreventiveRecallListTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { SentFaxInboxItem } from "../../Models/MyNotesInboxItem";
import { IConsultationRequestInboxModel } from "../../Models/UI/IConsultationRequestsInboxModel";
import { INotesInboxModel } from "../../Models/UI/INotesInboxModel";
import { IPendingApprovalInboxModel } from "../../Models/UI/IPendingApprovalInboxModel";
import { IPortalSyncModel } from "../../Models/UI/IPortalSyncModel";
import { ILabRequestsInboxModel } from "../../Models/UI/IRequestsInboxModel";
import { ISentFaxInboxModel } from "../../Models/UI/ISentFaxInboxModel";

class PortfolioAction extends BaseAction {
    private readonly helper = sessionStore.helper;

    private setRequestDataForNotes(model: INotesInboxModel, printConfig: ColumnConfigurationModel[] = null): any {
        let tableColumnInfo: TableColumnInfo[] = null;
        if (printConfig != null) {
            tableColumnInfo = printConfig.map(x => new TableColumnInfo(x));
        }

        const requestData = {
            FromDate: null,
            ToDate: null,
            SiteIds: [],
            StatusApproved: false,
            StatusNotApproved: false,
            StatusSendForApproval: false,
            StatusReturned: false,
            EmployeeIds: [],
            SearchText: "",
            PageNumber: 0,
            PageSize: 0,
            OrderByColumn: model.get("OrderByColumn"),
            OrderByDirection: model.get("OrderByDirection"),
            ColumnConfiguration: tableColumnInfo
        };

        if (model.get("DateFrom") != null) {
            requestData.FromDate = MyleHelper.GetTimezoneAgnosticDatetimeFormat(model.get("DateFrom"));
        }

        if (model.get("DateTo") != null) {
            requestData.ToDate = MyleHelper.GetTimezoneAgnosticDatetimeFormat(model.get("DateTo"));
        }

        requestData.SearchText = model.get("GlobalPortfolioSearchText");
        requestData.PageSize = model.get("SelectedPageSizeOption");
        requestData.PageNumber = model.get("SelectedPageNumber");

        requestData.EmployeeIds = this.helper
            .GetCheckedItemsFromICheckableList(model.get("CliniciansGlobalCache"))
            .toArray()
            .filter(x => x.Checked)
            .map(x => (<EmployeeUIModel>x).Model.ContactFacet_id);

        if (model.get("ShowOnlySelectedSites")) {
            requestData.SiteIds = this.helper
                .GetCheckedItemsFromICheckableList(model.get("Sites"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => x.Id);
        }
        else
            requestData.SiteIds = model.get("Sites").toArray().map(x => x.Id);

        model.get("Status").forEach(status => {
            if (status.Id === "status_approved")
                requestData.StatusApproved = status.Checked;
            if (status.Id === "status_not_approved")
                requestData.StatusNotApproved = status.Checked;
            if (status.Id === "status_send_for_approval")
                requestData.StatusSendForApproval = status.Checked;
            if (status.Id === "status_returned")
                requestData.StatusReturned = status.Checked;
        });

        return requestData;
    }

    public async PrintNotes(model: INotesInboxModel, printConfig: ColumnConfigurationModel[]): Promise<void> {
        const requestData = this.setRequestDataForNotes(model, printConfig);

        const errorMessage = ValidationHelper.validateSearchDateRangeMax5Years(model.get("DateFrom"), model.get("DateTo"));

        if (errorMessage) {
            sessionStore.showPopup(errorMessage);
            return;
        }

        await this.ExecuteAsync(ServerOperations.Server.Myle_MyNotesGeneratePDF, requestData);
    }

    public LoadMyNotes(model: INotesInboxModel): void {
        const requestData = this.setRequestDataForNotes(model);
        this.Execute(ServerOperations.Server.Myle_LoadMyNotesData, requestData);
    }

    public GetApssCount(): void {
        if (!IsNullOrWhiteSpace(sessionStore.getCurrentEmployee().getActiveSite().Model.ClinicHistoricalIds)) {
            window.setTimeout(
                () => {
                    var op = new LocalEvent(LocalOperations.Portfolio.APSS_UpdateApssCount);
                    LocalEventDispatcher.dispatch(op);
                },
                50
            );
        }
    }

    public GetFailedFaxesCount(): void {
        window.setTimeout(
            () => {
                portfolioStore.initializeFaxPortfolioInbox(portfolioStore.Model.get("CurrentInbox"));
            },
            50
        );
    }

    public ToggleFailedFaxVisibility(ItemId: string, sentFaxId: number, value: boolean): void {
        var requestData = {
            ItemId: ItemId,
            SentFaxId: sentFaxId,
            Value: value
        };
        requestData.Value ? requestData.Value = false : requestData.Value = true;

        this.Execute(ServerOperations.Server.Myle_Portfolio_ToggleFailedFaxVisibilty, requestData);
    }

    public LoadMyConsultationRequests(Model: IConsultationRequestInboxModel): void {
        const requestData = {
            CreationFromDate: Model.get("CreationDateFrom") || null,
            CreationToDate: Model.get("CreationDateTo") || null,
            DueFromDate: Model.get("DueDateFrom") || null,
            DueToDate: Model.get("DueDateTo") || null,
            SearchText: Model.get("GlobalPortfolioSearchText") ?? "",
            PageNumber: Model.get("SelectedPageNumber") ?? 0,
            PageSize: Model.get("SelectedPageSizeOption") ?? 0,
            OrderByColumn: Model.get("OrderByColumn"),
            OrderByDirection: Model.get("OrderByDirection"),
            RequestingStaffIds: [],
            CreatedByIds: [],
            StatusList: [],
            PriorityList: []
        };

        requestData.RequestingStaffIds = Model.get("RequestingStaffList")
            .toArray()
            .filter(p => p.Checked)
            .map(p => p.Model.ContactFacet_id);

        requestData.CreatedByIds = Model.get("CreatedByClinicianList")
            .toArray()
            .filter(p => p.Checked)
            .map(p => p.ContactId);

        requestData.StatusList = Model.get("StatusList")
            .toArray()
            .filter(p => p.Checked)
            .map(p => p.Id);

        requestData.PriorityList = Model.get("PriorityList")
            .toArray()
            .filter(p => p.Checked)
            .map(p => p.Id);

        this.Execute(ServerOperations.Server.Myle_LoadMyConsultationRequestsData, requestData);
    }

    public LoadMyRequests(Model: ILabRequestsInboxModel): void {
        var requestData = {
            CreationFromDate: Model.get("CreationDateFrom") || null,
            CreationToDate: Model.get("CreationDateTo") || null,
            DueFromDate: Model.get("DueDateFrom") || null,
            DueToDate: Model.get("DueDateTo") || null,
            StatusUrgent: false,
            StatusNotUrgent: false,
            SearchText: Model.get("GlobalPortfolioSearchText"),
            PageNumber: Model.get("SelectedPageNumber"),
            PageSize: Model.get("SelectedPageSizeOption"),
            OrderByColumn: Model.get("OrderByColumn"),
            OrderByDirection: Model.get("OrderByDirection"),
            RequestingStaffIds: [],
            CreatedByIds: [],
            StatusList: [],
            PriorityList: []
        };

        requestData.RequestingStaffIds = Model.get("RequestingStaffList")
            .toArray()
            .filter(p => p.Checked)
            .map(p => p.Model.ContactFacet_id);

        requestData.CreatedByIds = Model.get("CreatedByClinicianList")
            .toArray()
            .filter(p => p.Checked)
            .map(p => p.ContactId);

        requestData.StatusList = Model.get("StatusList")
            .toArray()
            .filter(p => p.Checked)
            .map(p => p.Id);

        requestData.PriorityList = Model.get("PriorityList")
            .toArray()
            .filter(p => p.Checked)
            .map(p => p.Id);

        this.Execute(ServerOperations.Server.Myle_LoadExamRequests, requestData);
    }

    public LoadPortfolioFaxes(model: ISentFaxInboxModel): void {
        var requestData = {
            EmployeeIds: [],
            FromDate: null,
            ToDate: null,
            SearchText: "",
            SentFaxStatus: [],
            IncludeIgnored: model.get("IncludeIgnored"),
            SiteIds: [],
            PageNumber: 0,
            PageSize: 0,
            OrderByColumn: model.get("OrderByColumn"),
            OrderByDirection: model.get("OrderByDirection")
        };

        if (model.get("DateFrom") != null) {
            requestData.FromDate = MyleHelper.GetTimezoneAgnosticDatetimeFormat(moment(model.get("DateFrom")).startOf("day").toDate());
        }

        if (model.get("DateTo") != null) {
            requestData.ToDate = MyleHelper.GetTimezoneAgnosticDatetimeFormat(moment(model.get("DateTo")).endOf("day").toDate());
        }

        requestData.SearchText = model.get("GlobalPortfolioSearchText");
        requestData.PageSize = model.get("SelectedPageSizeOption");
        requestData.PageNumber = model.get("SelectedPageNumber");

        requestData.EmployeeIds = this.helper
            .GetCheckedItemsFromICheckableList(model.get("CliniciansGlobalCache"))
            .toArray()
            .filter(x => x.Checked)
            .map(x => (<EmployeeUIModel>x).Model.ContactFacet_id);

        if (model.get("ShowOnlySelectedSites")) {
            requestData.SiteIds = this.helper
                .GetCheckedItemsFromICheckableList(model.get("Sites"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => x.Id);
        }
        else {
            requestData.SiteIds = model.get("Sites").toArray().map(x => x.Id);
        }

        if (!model.get("SelectAllStatusChecked") && model.get("Status") != null && model.get("Status").count() > 0) {
            requestData.SentFaxStatus = this.helper
                .GetCheckedItemsFromICheckableList(model.get("Status"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => x.Id);
        }

        this.Execute(ServerOperations.Server.Myle_LoadMySentFaxData, requestData);
    }

    public LoadPortalSyncHistory(model: IPortalSyncModel): void {
        const settings = model.get("Settings");

        switch (portfolioStore.Model.get("CurrentInbox")) {
            case PortfolioInboxes.PortalSyncItemsErrors:
            case PortfolioInboxes.PortalPendingItems:
                this.Execute(
                    ServerOperations.Server.Myle_Portfolio_LoadPortalPendingItems,
                    {
                        PageSize: settings.get("PageSize"),
                        RowOffset: model.get("CurrentRowOffset"),

                        SearchText: settings.get("SearchText"),

                        DateFrom: settings.get("DateFrom") != null ? MyleHelper.GetTimezoneAgnosticDatetimeFormat(settings.get("DateFrom")) : null,
                        DateTo: settings.get("DateTo") != null ? MyleHelper.GetTimezoneAgnosticDatetimeFormat(settings.get("DateTo")) : null,

                        SiteIds: settings.get("Sites").toArray().filter(p => p.Checked).map(p => p.Id),
                        EmployeeIds: settings.get("Employees").toArray().filter(p => p.Checked).map(p => p.Id),
                        ItemTypes: settings.get("ItemTypes").toArray().filter(p => p.Checked).map(p => p.Id.replace("PortalSyncItemType_", "")),

                        OrderByDirection: settings.get("OrderByDirection"),
                        OrderByColumn: settings.get("OrderByColumn"),
                        GetErrorStatusOnly: model.get("GetErrorOnly")
                    }
                );
                break;

            case PortfolioInboxes.PortalSyncItems:
                this.Execute(
                    ServerOperations.Server.Myle_Portfolio_LoadPortalSyncHistory,
                    {
                        PageSize: settings.get("PageSize"),
                        RowOffset: model.get("CurrentRowOffset"),

                        SearchText: settings.get("SearchText"),

                        DateFrom: settings.get("DateFrom") != null ? MyleHelper.GetTimezoneAgnosticDatetimeFormat(settings.get("DateFrom")) : null,
                        DateTo: settings.get("DateTo") != null ? MyleHelper.GetTimezoneAgnosticDatetimeFormat(settings.get("DateTo")) : null,

                        SiteIds: settings.get("Sites").toArray().filter(p => p.Checked).map(p => p.Id),
                        EmployeeIds: settings.get("Employees").toArray().filter(p => p.Checked).map(p => p.Id),
                        ItemTypes: settings.get("ItemTypes").toArray().filter(p => p.Checked).map(p => p.Id.replace("PortalSyncItemType_", "")),
                        Statuses: settings.get("Statuses").toArray().filter(p => p.Checked).map(p => p.Id.replace("EnumPortalSyncStatus_", "")),

                        OrderByDirection: settings.get("OrderByDirection"),
                        OrderByColumn: settings.get("OrderByColumn")
                    }
                );
                break;

            default:
                throw Error(`LoadPortalSyncHistory called from non-portal sync page: ${portfolioStore.Model.get("CurrentInbox")}`);
        }
    }

    public RefreshSentFaxStatuses(ids: number[]): void {
        var requestData = {
            SentFaxIds: ids
        };

        this.Execute(ServerOperations.Server.Myle_Faxing_GetSentFaxStatus, requestData);
    }

    public LoadPatientInfo(PatientId: string, subOp: number): void {
        var requestData = {
            InactivableId: PatientId,
            IncludeRelatedRecalls: false
        };

        this.Execute(ServerOperations.Server.Myle_LoadPatient, requestData, subOp);
    }

    public DeleteSelectedRequest(RequestId: string): void {
        var requestData = {
            RequestId: RequestId
        };
        this.Execute(ServerOperations.Server.Myle_DeleteLabRequest, requestData, SubServerOperations.Portfolio.DeleteRequest);
    }

    public PrintSelectedRequest(requestId: string, includeSignature: boolean = false, justRerenderIframe?: boolean, faxName?: string, faxNumber?: string): void {
        let requestData;
        let faxComposite = {};
        if (justRerenderIframe)
            faxComposite = {
                RequestId: requestId,
                WithSignature: includeSignature,
                LabFax: faxNumber,
                LabName: faxName
            };

        requestData = {
            RequestId: requestId,
            AddSignature: includeSignature,
            FaxLabRequestComposite: faxComposite
        };

        this.Execute(
            ServerOperations.Server.Myle_PrintSelectedRequest,
            requestData,
            justRerenderIframe
                ? SubServerOperations.Portfolio.GenerateLabRequest_ForIFrameReRender
                : SubServerOperations.Portfolio.GenerateLabRequest_ForPrint
        );
    }

    public SendLabRequestFax(faxLabRequestComposite: FaxLabRequestComposite): void {
        var requestData = {
            FaxLabRequestComposite: faxLabRequestComposite
        };
        this.Execute(ServerOperations.Server.Myle_Faxing_SendLabRequest, requestData);
    }

    public GetNotesMedfarBills(encounterIds: string[]): void {
        const requestData = {
            EncounterIds: encounterIds
        };

        this.Execute(ServerOperations.Server.MedfarBilling_GetBill, requestData);
    }

    public LoadPortfolioCount(): void {
        var requestData = {
            IsResident: EmployeeTitleHelper.isStudent(sessionStore.myleEmployeeTitle)
        };

        this.Execute(ServerOperations.Server.Myle_CountPortfolios, requestData);
    }

    public LoadPendingApproval(model: IPendingApprovalInboxModel, isAfterAction: boolean = false): void {
        if (EmployeeTitleHelper.isStudent(sessionStore.myleEmployeeTitle)) {
            let requestData = {
                FromDate: null,
                ToDate: null,
                SiteIds: [],
                StatusPending: false,
                StatusReturned: false,
                StatusApproved: false,
                EmployeeIds: [],
                SearchText: "",
                PageNumber: 0,
                PageSize: 0,
                ResidentContactId: sessionStore.getCurrentEmployee().ContactId,
                IsAfterAction: isAfterAction,
                OrderByColumn: model.get("OrderByColumn"),
                OrderByDirection: model.get("OrderByDirection")
            };

            if (model.get("DateFrom") != null) {
                requestData.FromDate = model.get("DateFrom");
            }

            if (model.get("DateTo") != null) {
                requestData.ToDate = model.get("DateTo");
            }

            requestData.SearchText = model.get("SearchText");

            requestData.EmployeeIds = this.helper
                .GetCheckedItemsFromICheckableList(model.get("CliniciansGlobalCache"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => (<EmployeeUIModel>x).Model.Contact_id);

            if (model.get("ShowOnlySelectedSites")) {
                requestData.SiteIds = this.helper
                    .GetCheckedItemsFromICheckableList(model.get("Sites"))
                    .toArray()
                    .filter(x => x.Checked)
                    .map(x => x.Id);
            }
            else
                requestData.SiteIds = model.get("Sites").toArray().map(x => x.Id);

            model.get("Status").forEach(status => {
                if (status.Id === "status_pending")
                    requestData.StatusPending = status.Checked;
                if (status.Id === "status_returned")
                    requestData.StatusReturned = status.Checked;
                if (status.Id === "status_approved")
                    requestData.StatusApproved = status.Checked;
            });

            this.Execute(ServerOperations.Server.Myle_Resident_GetPendingApprovals, requestData);
        }
        else {
            let requestData = {
                FromDate: null,
                ToDate: null,
                SiteIds: [],
                StatusPending: false,
                StatusReturned: false,
                StatusApproved: false,
                EmployeeIds: [],
                SearchText: "",
                SupervisorContactId: sessionStore.getCurrentEmployee().ContactId,
                IsAfterAction: isAfterAction,
                OrderByColumn: model.get("OrderByColumn"),
                OrderByDirection: model.get("OrderByDirection")
            };

            if (model.get("DateFrom") != null) {
                requestData.FromDate = MyleHelper.GetTimezoneAgnosticDatetimeFormat(model.get("DateFrom"));
            }

            if (model.get("DateTo") != null) {
                requestData.ToDate = MyleHelper.GetTimezoneAgnosticDatetimeFormat(model.get("DateTo"));
            }

            requestData.SearchText = model.get("SearchText");

            requestData.EmployeeIds = this.helper
                .GetCheckedItemsFromICheckableList(model.get("CliniciansGlobalCache"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => (<EmployeeUIModel>x).Model.Contact_id);

            if (model.get("ShowOnlySelectedSites")) {
                requestData.SiteIds = this.helper
                    .GetCheckedItemsFromICheckableList(model.get("Sites"))
                    .toArray()
                    .filter(x => x.Checked)
                    .map(x => x.Id);
            }
            else
                requestData.SiteIds = model.get("Sites").toArray().map(x => x.Id);

            model.get("Status").forEach(status => {
                if (status.Id === "status_pending")
                    requestData.StatusPending = status.Checked;
                if (status.Id === "status_returned")
                    requestData.StatusReturned = status.Checked;
                if (status.Id === "status_approved")
                    requestData.StatusApproved = status.Checked;
            });

            this.Execute(ServerOperations.Server.Myle_Resident_GetPendingApprovals, requestData);
        }
    }

    public GeneratePendingApprovalNotePDF(encounterId: string, patientContactId: string): void {
        var requestData = {
            EncounterId: encounterId,
            PatientContactId: patientContactId,
            AddSignature: false,
            IsPreview: true
        };

        this.Execute(ServerOperations.Server.Myle_Patient_MedicalNote_GenerateNotePDF, requestData);
    }

    public GetPendingApprovalNotes(encounterId: string, patientContactId: string): void {
        var requestData = {
            EncounterId: encounterId,
            PatientContactId: patientContactId
        };

        this.Execute(ServerOperations.Server.Myle_Resident_LoadPendingApprovalNotes, requestData);
    }

    public SendPendingApproval(supervisorContactId: string, residentContactId: string, encounterId: string, message: string): void {
        var requestData = {
            SupervisorContactId: supervisorContactId,
            ResidentContactId: residentContactId,
            EncounterId: encounterId,
            Message: message
        };

        this.Execute(ServerOperations.Server.Myle_Resident_SendPendingApproval, requestData);
    }

    public ApprovePendingApproval(
        encounterId: string,
        message: string,
        additionalSignOffInformation: AdditionalSignOffInformation
    ): void {
        var requestData = {
            EncounterId: encounterId,
            AppointmentId: additionalSignOffInformation?.appointmentId,
            InterfaceNoteCategory: additionalSignOffInformation?.interfaceNoteCategory,
            ConsultationTypeId: sessionStore.getCurrentEmployeeDefaultSpecializationLookupId(),
            Message: message // when signing off, the encounter will take the specialization of the signingoff employee
        };

        this.Execute(
            ServerOperations.Server.Myle_Resident_ApprovePendingApproval,
            requestData,
            null,
            null,
            () => additionalSignOffInformation?.ValidationCallback?.(false), // on error
            () => additionalSignOffInformation?.ValidationCallback?.(true)
        );
    }

    public ReturnPendingApproval(encounterId: string, message: string): void {
        var requestData = {
            EncounterId: encounterId,
            Message: message,
            EmployeeContactId: sessionStore.getCurrentEmployee().ContactId
        };

        this.Execute(ServerOperations.Server.Myle_Resident_ReturnPendingApproval, requestData);
    }

    public FaxConsultationRequest(faxConsultationRequestComposite: FaxConsultationRequestComposite): void {
        var requestData = {
            FaxConsultationRequestComposite: faxConsultationRequestComposite
        };

        this.Execute(ServerOperations.Server.Myle_Faxing_SendConsultationRequest, requestData, SubServerOperations.Portfolio.FaxConsultationRequest);
    }

    public GetSentFaxPreview(sentFaxItem: SentFaxInboxItem, withSignature: boolean): void {
        var requestData = {
            SentFaxItemId: sentFaxItem.SentFaxId,
            WithSignature: withSignature
        };

        this.Execute(ServerOperations.Server.Portfolio_GetSentFaxPreview, requestData);
    }

    public async AttachAPSSFiles(requestId: string, attachmentCode: string, attachments: ExportableFile[]): Promise<void> {
        const { token } = await sessionStore.getAPSSToken();
        if (!token)
            return;

        this.Execute(
            ServerOperations.Server.Myle_UploadApssAttachments,
            {
                ApssCookie: token,
                ApssRequestId: requestId,
                AttachmentCode: attachmentCode,
                Attachments: attachments.map(x => x.toAttachedObject())
            }
        );
    }

    public loadSharedItem(sharedItemId: string, includeFiles: boolean, withSignature: boolean): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_Portal_GetSharedItem,
            {
                SharedItemId: sharedItemId,
                IncludeFiles: includeFiles,
                IncludeLinkedItem: true,
                WithSignature: withSignature
            },
            SubServerOperations.PortalLinkedItemStatus.Portfolio
        );
    }

    public revokePortalSharedItem(sharedItemId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem,
            { SharedItemId: sharedItemId }
        );
    }

    public updateSharedItem(
        sharedItemId: string,
        sharedItemType: PortalLinkedItemType,
        subject: string,
        patientContactId: string,
        portalMessages: SharedItemMessage[],
        includeLinkedItem: boolean,
        canPatientReplyWithMessage: boolean
    ): void {
        this.Execute(
            ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem,
            {
                SharedItemId: sharedItemId,
                SharedItemType: sharedItemType,
                Subject: subject,
                PatientContactId: patientContactId,
                Messages: portalMessages,
                IncludeLinkedItem: includeLinkedItem,
                CanPatientReplyWithMessage: canPatientReplyWithMessage
            }
        );
    }

    public loadPreventiveActivities(): void {
        const search = portfolioStore.preventiveActivitiesInboxModel.get("SavedSettingsContainerState");
        const statusIds = (search.statuses || []).filter(p => p.Checked).map(p => p.Id);

        const isToDoSelected = statusIds.includes(ScreeningTestStatus.Todo.LookupID);
        if (search.isOnlyWithinReminderPeriodChecked && !isToDoSelected && cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff) {
            statusIds.push(ScreeningTestStatus.Todo.LookupID);
        }

        const requestingStaffIds = cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff
            ? (search.globalRequestingStaffList || []).filter(p => p.Checked).map(p => p.ContactFacetId)
            : (search.globalClinicianList || []).filter(p => p.Checked).map(p => p.Id);

        const requestData = {
            RequestingStaffIds: requestingStaffIds,
            SiteIds: (search.sites || []).filter(p => p.Checked).map(p => p.Id),
            StatusList: !search.showAllStatuses ? statusIds : [],
            DueDateFrom: cacheStore.Helper.CreateDateAsUTC(search.dateFrom),
            DueDateTo: cacheStore.Helper.CreateDateAsUTC(search.dateTo),
            SearchText: portfolioStore.preventiveActivitiesInboxModel.get("SearchText"),
            PageNumber: portfolioStore.preventiveActivitiesInboxModel.get("CurrentPage"),
            PageSize: search.pageSize,
            OrderByColumn: portfolioStore.preventiveActivitiesInboxModel.get("OrderByColumn"),
            OrderByDirection: portfolioStore.preventiveActivitiesInboxModel.get("OrderByDirection"),
            ShouldFilterByReminderPeriod: cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff ? search.isOnlyWithinReminderPeriodChecked : false
        };
        this.Execute(ServerOperations.Server.Myle_Portfolio_LoadMyPreventiveActivities, requestData);
    }

    public loadPreventiveRecallList(): void {
        const search = portfolioStore.preventiveRecallListModel.get("SavedSettingsContainerState");

        const requestData = {
            RequestingStaffIds: (search.globalProvidersList || []).filter(p => p.Checked).map(p => p.ContactFacetId),
            PreventiveCareCriteriaId: search.PreventiveCareCriteriaId,
            CoverageDateFrom: search.dateFrom,
            CoverageDateTo: search.dateTo,
            SearchText: portfolioStore.preventiveRecallListModel.get("SearchText"),
            PageNumber: portfolioStore.preventiveRecallListModel.get("CurrentPage"),
            PageSize: search.pageSize,
            IncludeNonEnrolledPatients: search.includeNonEnrolledPatients
        };

        this.Execute(ServerOperations.Server.Myle_Portfolio_PreventiveCare_LoadRecallList, requestData);
    }

    public loadPreventiveCareNotices(patientContactFacetId: Guid, preventiveCareCriteriaId: Guid, employeeContactFacetId: Guid): void {
        const requestData = {
            PatientContactFacetId: patientContactFacetId,
            PreventiveCareCriteriaId: preventiveCareCriteriaId,
            EmployeeContactFacetId: employeeContactFacetId
        };

        this.Execute(ServerOperations.Server.Myle_Portfolio_PreventiveCare_LoadNotices, requestData);
    }

    public generateRecallListLetters(letters: Immutable.List<PreventiveRecallListTableItem>): void {
        const settings = portfolioStore.preventiveRecallListModel.get("SavedSettingsContainerState");

        const requestData = {
            PreventiveCareCriteriaId: settings.PreventiveCareCriteriaId,
            CoverageDateFrom: settings.dateFrom,
            LetterInfo: letters.map(item => ({
                EmployeeId: item.Model.EmployeeContactFacetId,
                PatientId: item.Model.PatientContactFacetId,
                LastProcedureDate: item.Model.LastProcedureDate
            })).toArray()
        };

        this.Execute(ServerOperations.Server.Myle_Portfolio_PreventiveCare_GenerateLetters, requestData);
    }

    public generateBonusReport(providerId: Guid, criteriaId: Guid): void {
        const dates = PreventiveCareActivitiesHelper.adjustFiscalYearDatesByFirstQuarterRule();
        const requestObject = {
            PreventiveCareCriteriaId: criteriaId,
            CoverageDateFrom: dates.dateFrom,
            CoverageDateTo: dates.dateTo,
            requestingStaffId: providerId
        };

        this.Execute(ServerOperations.Server.Myle_Portfolio_PreventiveCare_GenerateBonus, requestObject);
    }
}

export { PortfolioAction };
